// craftRulesPump.js — WIRE-Electron: the renderer pump that closes the craft loop.
//
// After a human APPROVES a craft opinion (CraftReview tab), the Worker has the
// gated learned_rule yaml; this pump applies the rule to .apolorules (reusing
// applyLearnedRules → applyLearned IPC + git commit + session sync) and
// materializes generated craft tests to disk (via the existing apolo:file:write
// handler), then reports each outcome back to the Worker. THE LAW: only
// human-approved rules reach here — the pump is the off-band file step, never an
// auto-decision. Every failure is reported VISIBLY (never silent).
//
// Defense-in-depth: the pump applies a rule ONLY when the Worker persisted a gated
// promotionYaml (the integrity guard passed on approval); on web (no Electron
// bridge) it no-ops instead of failing every cycle; and `generated_at` is STABLE
// per item so a re-applied rule dedupes byte-identically (no duplicate commit).

import { apiFetch } from '../api.js';
import { applyLearnedRules } from '../../hooks/useEvaluator.js';

function stableGeneratedAt(item) {
  // Stable per item (not per pump cycle) so a retry renders the SAME LEARNED block
  // → applyLearnedRulesAtomic returns { changed:false } → no second chore(learn) commit.
  const ms = Number(item?.createdAt);
  if (Number.isFinite(ms) && ms > 0) {
    try { return new Date(ms).toISOString(); } catch { /* fall through */ }
  }
  return '1970-01-01T00:00:00.000Z';
}

/**
 * Apply approved craft rules + materialize generated craft tests for one project.
 * Deps are injectable for tests; defaults use the live API + Electron bridges.
 *
 * @param {{ root?: string, sessionId?: string,
 *           fetchImpl?: typeof apiFetch,
 *           applyRule?: typeof applyLearnedRules,
 *           writeFile?: (p: { root: string, filePath: string, content: string }) => Promise<{ written?: boolean, error?: string }> }} opts
 */
export async function pumpApprovedCraftRules({
  root,
  sessionId,
  fetchImpl = apiFetch,
  applyRule = applyLearnedRules,
  writeFile,
} = {}) {
  const empty = { rules: { processed: 0, applied: 0, failed: 0 }, tests: { processed: 0, written: 0, failed: 0 } };
  if (!root) return { skipped: 'no-root', ...empty };

  // On the web (no Electron bridge) the live writers can't reach the filesystem —
  // no-op instead of reporting a failure on every poll cycle.
  const usingDefaults = applyRule === applyLearnedRules && !writeFile;
  const bridge = globalThis.window?.apolo;
  if (usingDefaults && !bridge?.apolorules) return { skipped: 'no-electron-bridge', ...empty };

  const fileWrite = writeFile || ((p) => bridge?.file?.write?.(p));
  const rules = { processed: 0, applied: 0, failed: 0 };
  const tests = { processed: 0, written: 0, failed: 0 };

  // 1. Approved rules → .apolorules (reuse the learned-rule writer + commit + sync).
  const rulesRes = await fetchImpl('/api/evaluator/pending-rules');
  if (rulesRes?.ok) {
    for (const item of rulesRes.data?.items || []) {
      rules.processed += 1;
      // Defense-in-depth: only items the Worker actually gated (promotionYaml set)
      // are applied. An approved item without it never passed promoteToLearnedRule.
      if (!item.promotionYaml) {
        rules.failed += 1;
        await fetchImpl(`/api/evaluator/review-queue/${item.id}/apply-failed`, { method: 'POST', body: { error: 'no gated promotion yaml — refusing to apply' } });
        continue;
      }
      const ruleId = item.verdictId || item.id;
      const ruleRecord = { rule_id: ruleId, rule: item.suggestion, n: 1, ext_pct: 100, generated_at: stableGeneratedAt(item) };
      let res;
      try {
        res = await applyRule(root, [ruleRecord], ruleId, { sessionId });
      } catch (e) {
        res = { ok: false, error: e?.message || 'apply threw' };
      }
      if (res?.ok) {
        // Only claim 'applied' once the Worker confirms the bookkeeping POST. If it
        // fails, leave applied_at NULL → next cycle re-applies idempotently (stable
        // generated_at → changed:false → no second commit) and retries the POST.
        const mark = await fetchImpl(`/api/evaluator/review-queue/${item.id}/applied`, { method: 'POST' });
        if (mark?.ok) rules.applied += 1; else rules.failed += 1;
      } else {
        rules.failed += 1;
        await fetchImpl(`/api/evaluator/review-queue/${item.id}/apply-failed`, { method: 'POST', body: { error: res?.error || 'apply failed' } });
      }
    }
  }

  // 2. Generated craft tests → disk (path-safe via apolo:file:write).
  const testsRes = await fetchImpl('/api/evaluator/craft-tests');
  if (testsRes?.ok) {
    for (const t of testsRes.data?.tests || []) {
      tests.processed += 1;
      try {
        const w = await fileWrite({ root, filePath: t.generatedTestPath, content: t.testSource });
        if (w?.written) {
          const mark = await fetchImpl(`/api/evaluator/craft-tests/${t.id}/written`, { method: 'POST' });
          if (mark?.ok) tests.written += 1; else tests.failed += 1; // POST failed → retry next cycle
        } else {
          tests.failed += 1;
          await fetchImpl(`/api/evaluator/craft-tests/${t.id}/write-failed`, { method: 'POST', body: { error: w?.error || 'write failed (no bridge?)' } });
        }
      } catch (e) {
        tests.failed += 1;
        await fetchImpl(`/api/evaluator/craft-tests/${t.id}/write-failed`, { method: 'POST', body: { error: e?.message || 'write threw' } });
      }
    }
  }

  return { rules, tests };
}
